import PropTypes from 'prop-types';
import React from 'react';
import {
    Button as RACButton,
    OverlayArrow as RACOverlayArrow,
    Tooltip as RACTooltip,
    TooltipTrigger as RACTooltipTrigger,
} from 'react-aria-components';

import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';

import styles from './Tooltip.module.scss';

const TooltipContent = React.forwardRef(({ content, ...props }, ref) => {
    return (
        <RACTooltip {...props} className={styles[`tooltip__content-container`]} ref={ref}>
            <RACOverlayArrow>
                <svg width={16} height={12} viewBox="0 0 16 16">
                    <path d="M0 0 L8 6 L16 0" />
                </svg>
            </RACOverlayArrow>
            <div className={styles[`tooltip__content`]}>
                <RichTextEditor text={content} />
            </div>
        </RACTooltip>
    );
});

const Tooltip = ({
    tooltipContent,
    iconComponent: Icon,
    trigger,
    buttonArialLabel,
    placement = 'bottom',
    ...props
}) => {
    const tooltipRef = React.useRef(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const handleKeyDown = React.useCallback(
        (e) => {
            switch (e.key) {
                case 'Escape': {
                    if (isOpen) {
                        setIsOpen(false);
                    }
                    break;
                }
                default:
                    break;
            }
        },
        [isOpen],
    );

    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (!tooltipRef?.current?.contains(e.target)) {
                setIsOpen(false);
            }
        };
        window.addEventListener('click', handleClickOutside);

        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!Icon) return null;

    return trigger === 'click' ? (
        <RACTooltipTrigger isOpen={isOpen} {...props}>
            <RACButton
                className={styles[`tooltip__button`]}
                onKeyDown={handleKeyDown}
                onPress={() => {
                    setIsOpen(!isOpen);
                }}
                aria-label={buttonArialLabel}
            >
                <Icon size="medium" />
            </RACButton>
            <TooltipContent content={tooltipContent} placement={placement} ref={tooltipRef} />
        </RACTooltipTrigger>
    ) : (
        <RACTooltipTrigger trigger={trigger} {...props}>
            <RACButton className={styles[`tooltip__button`]}>
                <Icon size="medium" />
            </RACButton>
            <TooltipContent content={tooltipContent} placement={placement} />
        </RACTooltipTrigger>
    );
};

Tooltip.propTypes = {
    /**
     * tooltip content html
     */
    tooltipContent: PropTypes.string,
    /**
     * Icon
     */
    iconComponent: PropTypes.elementType,
    /**
     * aria-label of the button
     */
    buttonArialLabel: PropTypes.string,
    /**
     * display tooltip on 'click', 'hover', 'focus'
     */
    trigger: PropTypes.string,
    /**
     * placement of the tooltip content with respect to the button: 'start' | 'end' | 'right' | 'left' | 'top' | 'bottom'
     */
    placement: PropTypes.string,
};

export default Tooltip;
